import { CreateTableOutput } from 'aws-sdk/clients/dynamodb';
import { AWSError } from 'aws-sdk/lib/error';
import { PromiseResult } from 'aws-sdk/lib/request';
import { mapValues } from 'lodash';
import { logger } from '@meshwatch/logging';

import {
  DEFAULT_DYNAMO_DB_CLIENT,
  DynamoDBClient,
  DynamoDBTableClient,
} from '../../shared/aws/dynamodb';
import { BadRequestException, NotFoundException } from '../../shared/rest';
import DateUtils from '../../util/date';
import { MONITORING_TABLE_SCHEMA } from '../../monitoring/db/schema';
import DynamoMonitoringDatasource from '../../monitoring/db/datasource';

import {
  BaseUserInfo,
  CompleteGettingStartedTaskPayload,
  INITIAL_USER_INFO,
  UserInfo,
  GettingStarted,
} from './types';
import { USER_INFO_SORT_KEY, completeGettingStartedTaskUpdate } from './model';

export interface UserDatasource {
  createTable: () => Promise<PromiseResult<CreateTableOutput, AWSError>>;
  getUserInfo(userId: string): Promise<UserInfo>;
  completeGettingStartedTask(params: CompleteGettingStartedTaskPayload): Promise<UserInfo>;
  insertUserInfoRow(Item: UserInfoDynamoRow): Promise<UserInfo>;
}

export class DynamoUserDatasource implements UserDatasource {
  private dynamoClient: DynamoDBTableClient;

  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    this.dynamoClient = new DynamoDBTableClient(MONITORING_TABLE_SCHEMA.TableName, dynamoClient);
  }

  public createTable = (): Promise<PromiseResult<CreateTableOutput, AWSError>> => {
    return this.dynamoClient.createTable(MONITORING_TABLE_SCHEMA);
  };

  public getUserInfo = (userId: string): Promise<UserInfo> => {
    const hashKey = DynamoMonitoringDatasource.encodeHashKey({ userId });
    return this.dynamoClient
      .queryTable({
        KeyConditionExpression: `hashKey = :hashKey and sortKey = :sortKey`,
        ExpressionAttributeValues: {
          ':hashKey': hashKey,
          ':sortKey': USER_INFO_SORT_KEY,
        },
      })
      .then(resp => {
        if (!resp.Items) {
          throw new NotFoundException(`Could not find userInfo for user: ${userId}`);
        } else if (resp.Items.length > 0) {
          return this.userInfoFromDynamoRow(resp.Items[0] as UserInfoDynamoRow);
        }

        if ('development' === process.env.NODE_ENV) {
          logger.debug(
            `No userInfo found for: ${userId}. Creating initial userInfo: `,
            INITIAL_USER_INFO
          );
        }

        return this.insertUserInfoRow({
          hashKey: DynamoMonitoringDatasource.encodeHashKey({ userId }),
          sortKey: USER_INFO_SORT_KEY,
          ...INITIAL_USER_INFO,
        });
      });
  };

  public completeGettingStartedTask = (
    params: CompleteGettingStartedTaskPayload
  ): Promise<UserInfo> => {
    const { taskName, completedOn, userId } = params;
    return this.dynamoClient
      .updateItem({
        ...completeGettingStartedTaskUpdate(userId, completedOn, taskName),
        ReturnValues: 'UPDATED_NEW',
      })
      .then(resp => {
        if (!resp.Attributes) {
          throw new Error(`Could not update completeGettingStartedTask, userId: ${userId}`);
        }
        const attributes = resp.Attributes as { gettingStarted: GettingStarted<number> };
        return this.userInfoFromDynamoRow({
          hashKey: DynamoMonitoringDatasource.encodeHashKey({ userId }),
          sortKey: USER_INFO_SORT_KEY,
          ...attributes,
        });
      })
      .catch(err => {
        logger.error('Could not update completeGettingStartedTask', err, { params });
        if (err.name === 'ConditionalCheckFailedException') {
          throw new BadRequestException(`Task [${taskName}] is already completed`);
        } else if (
          err.name === 'ValidationException' &&
          err.message ===
            'The document path provided in the update expression is invalid for update'
        ) {
          throw new NotFoundException(
            'Getting started task cannot be completed before userInfo is created'
          );
        }
        throw err;
      });
  };

  public insertUserInfoRow = (Item: UserInfoDynamoRow): Promise<UserInfo> => {
    return this.dynamoClient
      .putDocumentToDynamo({ Item })
      .then(_ => this.userInfoFromDynamoRow(Item));
  };

  private userInfoFromDynamoRow = (dynamoRow: UserInfoDynamoRow): UserInfo => {
    const { userId } = DynamoMonitoringDatasource.decodeHashKey(dynamoRow.hashKey);
    const completedTasks = mapValues(
      dynamoRow.gettingStarted.completedTasks,
      DateUtils.dateFromUnixTimestamp
    );

    return {
      userId,
      gettingStarted: {
        ...dynamoRow.gettingStarted,
        completedTasks,
      },
    };
  };
}

interface UserInfoDynamoRow extends BaseUserInfo<number> {
  hashKey: string;
  sortKey: typeof USER_INFO_SORT_KEY;
}

const dynamoUserDatasource = new DynamoUserDatasource();

export default dynamoUserDatasource;
